/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useHistory} from 'react-router-dom'
import {isServer} from '@app/utils/utils'

// Components
import {
    AspectRatio,
    Box,
    Skeleton as ChakraSkeleton,
    Text,
    HStack,
    Stack,
    useMultiStyleConfig,
    IconButton,
    List,
    ListItem,
    useBreakpointValue,
    Badge
} from '@salesforce/retail-react-app/app/components/shared/ui'
import DynamicImage from '@app/components/dynamic-image'
import {WrapperBadgeLink} from '../wrapper-badge-link'
import useMultiSite from '../../hooks/use-multi-site'
import {useTheme} from '@chakra-ui/react'

// Icons
import {WishlistIcon, WishlistFilledIcon} from '../custom-icons'

// Project Components
import Link from '../link'
import withRegistration from '@salesforce/retail-react-app/app/components/with-registration'
import PromoCallout from './promo-callout'
import DisplayPrice from '../display-price'
import ShopLookDrawer from '../product-view/ShopLookDrawer'

// Hooks
import {FormattedMessage, useIntl} from 'react-intl'

// OOTB Utils
import {
    productUrlBuilder,
    removeQueryParamsFromPath
} from '@salesforce/retail-react-app/app/utils/url'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'

// Custom hooks
import {useGTM} from '@app/hooks/use-gtm'

// Other
import {PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID} from '@salesforce/retail-react-app/app/constants'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {
    filterImageGroups,
    getDecoratedVariationAttributes
} from '@salesforce/retail-react-app/app/utils/product-utils'
import {
    groupVariationValues,
    getPriceData,
    getProductType,
    createAutomatedLinkForTag
} from '../../utils/product-utils'
import {productSetURLBuilder} from '../../utils/seo-utils'
import {COLOR_ATTRIBUTE_ID, SIZE_ATTRIBUTE_ID} from '../../constants'

const IconButtonWithRegistration = withRegistration(IconButton)

/**
 * CompleteTheLook is a React functional component that renders a ShopLookDrawer component.
 * It is designed to display a curated selection of products that complement a given product,
 * encouraging users to "complete the look."
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.product - The main product to be featured in the "Complete the Look" section.
 *
 * @returns {JSX.Element} A JSX element representing the ShopLookDrawer with the specified product and UI configurations.
 *
 * @component
 * @example
 * const product = { id: 1, name: 'Stylish Jacket' };
 * return <CompleteTheLook product={product} />;
 */
const CompleteTheLook = ({product, isQuickShop}) => {
    const intl = useIntl()
    return (
        <ShopLookDrawer
            products={[product]}
            isQuickShop={isQuickShop}
            mainTitle={intl.formatMessage({
                id: 'product_detail.recommended_products.title.complete_set',
                defaultMessage: 'Complete the look'
            })}
            buttonProps={{
                styles: 'completeLookDrawerButton',
                text: (
                    <FormattedMessage
                        id="product_set_drawer.header.quick_shop"
                        defaultMessage="Quick shop"
                    />
                )
            }}
        />
    )
}

CompleteTheLook.propTypes = {
    product: PropTypes.object,
    isQuickShop: PropTypes.bool
}

// Component Skeleton
const PricingAndPromotionsSkeleton = () => {
    return (
        <Stack
            pt={10}
            px={10}
            gap={3}
            data-testid="sf-product-tile-pricing-and-promotions-skeleton"
        >
            <ChakraSkeleton width="80px" height="16px" />
            <ChakraSkeleton width={{base: '120px', md: '220px'}} height="16px" />
        </Stack>
    )
}

export const Skeleton = ({...props}) => {
    const styles = useMultiStyleConfig('ProductTile')
    return (
        <Box data-testid="sf-product-tile-skeleton" {...props}>
            <Stack spacing={2}>
                <Box {...styles.imageWrapper}>
                    <AspectRatio ratio={4 / 5} {...styles.image}>
                        <ChakraSkeleton />
                    </AspectRatio>
                </Box>
                <PricingAndPromotionsSkeleton />
            </Stack>
        </Box>
    )
}

/**
 * FavouriteIcon is a React functional component that renders a toggleable icon button
 * for adding or removing a product from the user's wishlist. It provides an accessible
 * interface with assistive messages for screen readers and handles click events to
 * prevent unwanted interactions with underlying elements.
 *
 * @param {Object} props - The properties object.
 * @param {boolean} props.isFavourite - A boolean indicating whether the product is currently marked as a favourite.
 * @param {string} props.localizedProductName - The localized name of the product, used in assistive messages.
 * @param {Function} props.onFavouriteToggle - A callback function that is triggered when the favourite status is toggled.
 * @param {Object} props.styles - An object containing style properties to be applied to the icon button.
 * @param {Object} props.props - Additional properties to be spread onto the Box component.
 *
 * @returns {JSX.Element} A JSX element representing the favourite icon button.
 */
export const FavouriteIcon = ({
    isFavourite,
    localizedProductName,
    onFavouriteToggle,
    styles,
    ...props
}) => {
    const intl = useIntl()
    const isFavouriteLoading = useRef(false)

    return (
        <Box
            onClick={(e) => {
                // stop click event from bubbling
                // to avoid user from clicking the underlying
                // product while the favourite icon is disabled
                e.preventDefault()
            }}
            {...props}
            {...styles}
        >
            <IconButtonWithRegistration
                data-testid="wishlist-button"
                aria-label={
                    isFavourite
                        ? intl.formatMessage(
                              {
                                  id: 'product_tile.assistive_msg.remove_from_wishlist',
                                  defaultMessage: 'Remove {product} from wishlist'
                              },
                              {product: localizedProductName}
                          )
                        : intl.formatMessage(
                              {
                                  id: 'product_tile.assistive_msg.add_to_wishlist',
                                  defaultMessage: 'Add {product} to wishlist'
                              },
                              {product: localizedProductName}
                          )
                }
                icon={
                    isFavourite ? (
                        <WishlistFilledIcon boxSize="12px" />
                    ) : (
                        <WishlistIcon boxSize="12px" />
                    )
                }
                onClick={async () => {
                    if (!isFavouriteLoading.current) {
                        isFavouriteLoading.current = true
                        await onFavouriteToggle(!isFavourite)
                        isFavouriteLoading.current = false
                    }
                }}
            />
        </Box>
    )
}

FavouriteIcon.propTypes = {
    isFavourite: PropTypes.bool,
    localizedProductName: PropTypes.string,
    onFavouriteToggle: PropTypes.func,
    styles: PropTypes.object
}

/**
 * The ProductTile is a simple visual representation of a
 * product object. It will show it's default image, name and price.
 * It also supports favourite products, controlled by a heart icon.
 */
const ProductTile = (props) => {
    const {
        dynamicImageProps,
        isFavourite,
        onFavouriteToggle,
        product,
        selectableAttributeId = PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID,
        isRefreshingData = false,
        isSwitchedImages = false,
        mainImageType,
        hoverImageType,
        index = 0,
        listHasNoMargin = false,
        focusRef,
        showProductImages = true,
        showProductNames = true,
        showProductPrices = true,
        showProductColorVariations = true,
        isFavIconInTitle = true,
        isPriceLarge = true,
        recommender,
        isImageLazyLoading = true,
        titleHeading = 'h2',
        isCompleteTheLook = false,
        ...rest
    } = props
    const {imageGroups = [], productId, representedProduct, variants} = product || {}

    const intl = useIntl()
    const {currency} = useCurrency()
    const history = useHistory()
    const [isTileActive, setIsTileActive] = useState(false)
    const {triggerViewItemEvent, triggerClickRelatedProductsEvent, triggerClickFamilyLabel} =
        useGTM()
    const appOrigin = useAppOrigin()
    const styles = useMultiStyleConfig('ProductTile')
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const nbProductsPerRow = useBreakpointValue({
        base: 2,
        sm: 2,
        md: 3,
        lg: 4,
        xl: 4,
        '2xl': 5
    })

    const productType = getProductType(product)
    const isMasterVariant = !!variants
    let selectableAttributeValue = undefined
    if (isMasterVariant && !!representedProduct) {
        const foundVariant = variants?.find(
            (variant) => variant.productId == product?.representedProduct?.id
        )
        if (foundVariant) {
            selectableAttributeValue = foundVariant.variationValues?.[selectableAttributeId]
        }
    } else if (isMasterVariant) {
        const foundVariant = variants?.find(
            (variant) =>
                variant?.variationValues?.[selectableAttributeId] ==
                product?.variationValues?.[selectableAttributeId]
        )
        if (foundVariant) {
            selectableAttributeValue = foundVariant.variationValues?.[selectableAttributeId]
        }
    }

    // Primary image for the tile, the image is determined from the product and selected variation attributes.
    const image = useMemo(() => {
        // NOTE: If the selectable variation attribute doesn't exist in the products variation attributes
        // array, lets not filter the image groups on it. This ensures we always return an image for non-variant
        // type products.
        const hasSelectableAttribute =
            // variationAttributes are only defined for master/variant type products
            product?.variationAttributes?.find(({id}) => id === selectableAttributeId) ||
            // filter images for variation_group catalog structure
            product?.variationGroups?.length > 0

        const variationValues = {[selectableAttributeId]: selectableAttributeValue}
        const overrideImageGroups = filterImageGroups(imageGroups, {
            viewType: 'override',
            variationValues: hasSelectableAttribute ? variationValues : {}
        })

        const frontImageGroups = filterImageGroups(imageGroups, {
            viewType: 'front',
            variationValues: hasSelectableAttribute ? variationValues : {}
        })

        const lifestyleImageGroups = filterImageGroups(imageGroups, {
            viewType: 'lifestyle',
            variationValues: hasSelectableAttribute ? variationValues : {}
        })

        const frontwornImageGroups = filterImageGroups(imageGroups, {
            viewType: 'frontworn',
            variationValues: hasSelectableAttribute ? variationValues : {}
        })

        let mainImage, hoverImage
        if (product?.hitType === 'set') {
            mainImage = imageGroups?.find((group) => group.viewType === 'look1')?.images[0]
            hoverImage = imageGroups?.find((group) => group.viewType === 'look2')?.images[0]
        } else if (mainImageType && hoverImageType) {
            // Use image type from props to determine image groups to use
            const mainImageGroup =
                mainImageType === 'frontworn'
                    ? frontwornImageGroups
                    : mainImageType === 'lifestyle'
                    ? lifestyleImageGroups
                    : frontImageGroups
            mainImage = mainImageGroup?.[0]?.images[0]
            const hoverImageGroup =
                hoverImageType === 'frontworn'
                    ? frontwornImageGroups
                    : hoverImageType === 'lifestyle'
                    ? lifestyleImageGroups
                    : frontImageGroups
            hoverImage = hoverImageGroup?.[0]?.images[0]
        } else if (overrideImageGroups?.[0]?.images[0]) {
            // Use override as main and front as hover if override exists
            mainImage = overrideImageGroups[0].images[0]
            hoverImage = frontImageGroups?.[0]?.images[0]
        } else {
            // Default logic: front as main, lifestyle/frontworn as hover
            mainImage = frontImageGroups?.[0]?.images[0]
            hoverImage =
                lifestyleImageGroups?.[0]?.images[0] || frontwornImageGroups?.[0]?.images[0]
        }

        const optimizedImages =
            isSwitchedImages && hoverImage
                ? {mainImage: hoverImage, hoverImage: mainImage}
                : {mainImage, hoverImage}

        // Return an object containing both main and hover images
        return optimizedImages
    }, [product, selectableAttributeId, selectableAttributeValue])

    // Primary URL user to wrap the ProduceTile.
    const productUrl = useMemo(
        () =>
            product?.hitType === 'set'
                ? productSetURLBuilder(product?.productId)
                : productUrlBuilder({id: productId || product?.id}),
        [product]
    )

    // NOTE: variationAttributes are only defined for master/variant type products
    // OOTB: getDecoratedVariationAttributes is for "slicing_group" hierarchy
    // groupVariationValues is for new project product "variation_group" hierarchy
    const variationAttributes = useMemo(() => {
        return product?.hitType === 'slicing_group'
            ? getDecoratedVariationAttributes(product)
            : groupVariationValues(product)
    }, [product])

    // ProductTile is used by two components, RecommendedProducts and ProductList.
    // RecommendedProducts provides a localized product name as `name` and non-localized product
    // name as `productName`. ProductList provides a localized name as `productName` and does not
    // use the `name` property.
    const localizedProductName = product?.name ?? product?.productName

    const productWithFilteredVariants = useMemo(() => {
        const variants = product?.variants?.filter(
            ({variationValues}) =>
                variationValues[selectableAttributeId] === selectableAttributeValue
        )
        return {
            ...product,
            variants
        }
    }, [product, selectableAttributeId, selectableAttributeValue])

    // Pricing is dynamic! Ensure we are showing the right price for the selected variation attribute
    // value.
    const priceData = useMemo(() => {
        return getPriceData(productWithFilteredVariants)
    }, [productWithFilteredVariants])

    const paddingLeft = listHasNoMargin && index % nbProductsPerRow === 0 ? 10 : 0
    const paddingRight = listHasNoMargin && (index + 1) % nbProductsPerRow === 0 ? 10 : 0

    /**
     * Handles the click event on a link within the product set page.
     *
     * This function triggers a 'select_item' event using the `triggerViewItemEvent` function,
     * passing the `productSetData` as an argument. It is typically used to track user interactions
     * with individual items in a product set, allowing for analytics and user behavior insights.
     *
     * @function handleLinkClick
     */
    const handleLinkClick = () => {
        triggerViewItemEvent('select_item', product)
        if (recommender !== undefined) {
            const finalProduct = product
            finalProduct.productUrl = `${appOrigin}${history?.location?.pathname}`
            const event =
                recommender == 'complete-the-set'
                    ? 'click_complete_your_look'
                    : 'click_related_products'
            triggerClickRelatedProductsEvent(finalProduct, event)
        }
    }

    const {buildUrl} = useMultiSite()
    const automatedTagLink = buildUrl(createAutomatedLinkForTag(product.representedProduct))

    const productAriaLabel = intl.formatMessage({
        defaultMessage: 'Product',
        id: 'card.product.aria.label'
    })

    const handleColorSwatchesClick = (e) => {
        const encodedLink = e.currentTarget.getAttribute('data-url')

        if (encodedLink) {
            try {
                const decodedLink = atob(encodedLink)
                history.push(buildUrl(decodedLink))
            } catch (error) {
                // do nothing
            }
        }
    }

    const theme = useTheme()
    const baseLinkStyles = theme.components?.Link?.baseStyle || {}

    return (
        <Box
            sx={{
                ...styles.container,
                '.tilescolors + .tilesfavorite': {bottom: '16px'}
            }}
            role="group"
            aria-label={productAriaLabel}
            data-focusable-block
            tabIndex="0"
        >
            <Link
                data-testid="product-tile"
                to={productUrl}
                {...styles.link}
                {...rest}
                ref={focusRef}
                onClick={handleLinkClick}
            >
                {showProductImages && (image?.mainImage?.disBaseLink || image?.mainImage?.link) && (
                    <Box {...styles.imageWrapper}>
                        {/* Main image */}
                        <AspectRatio
                            ratio={4 / 5}
                            {...styles.image}
                            {...(isDesktop && image?.hoverImage && {className: 'main-image'})}
                            {...(isDesktop &&
                                image?.hoverImage &&
                                isTileActive && {display: 'none'})}
                        >
                            <DynamicImage
                                data-testid="product-tile-image"
                                src={`${
                                    image?.mainImage?.disBaseLink || image?.mainImage?.link
                                }[?sw={width}&q=60]`}
                                widths={dynamicImageProps?.widths}
                                imageProps={{
                                    alt: image?.mainImage?.alt || '',
                                    loading: isImageLazyLoading ? 'lazy' : 'eager',
                                    fetchpriority: isImageLazyLoading ? null : 'high',
                                    margin: '0 auto',
                                    aspectRatio: '4 / 5',
                                    ...dynamicImageProps?.imageProps
                                }}
                            />
                        </AspectRatio>

                        {/* Hover image on desktop only */}
                        {isDesktop && image?.hoverImage && (
                            <AspectRatio
                                ratio={4 / 5}
                                className="hover-image"
                                {...styles.image}
                                {...styles.hoverImage}
                                {...(isTileActive && {display: 'block'})}
                            >
                                <DynamicImage
                                    data-testid="product-tile-image"
                                    src={`${
                                        image?.hoverImage?.disBaseLink || image?.hoverImage?.link
                                    }[?sw={width}&q=60]`}
                                    widths={dynamicImageProps?.widths}
                                    imageProps={{
                                        alt: image?.hoverImage?.alt || '',
                                        loading: 'lazy',
                                        margin: '0 auto',
                                        aspectRatio: '4 / 5',
                                        ...dynamicImageProps?.imageProps
                                    }}
                                />
                            </AspectRatio>
                        )}
                    </Box>
                )}

                <Box pl={paddingLeft} pr={paddingRight}>
                    {/* Title */}
                    {showProductNames && (
                        <Text
                            textStyle="eyeBrowSmall"
                            textTransform="uppercase"
                            pt={10}
                            role="heading"
                            aria-level="2"
                        >
                            {localizedProductName}
                        </Text>
                    )}

                    {/* Price */}
                    {showProductPrices &&
                        (isRefreshingData ? (
                            <PricingAndPromotionsSkeleton />
                        ) : (
                            <Stack pt={10} gap={3}>
                                {/* Price */}
                                <DisplayPrice
                                    priceData={priceData}
                                    currency={currency}
                                    promoCallout={
                                        shouldShowPromoCallout(productWithFilteredVariants) ? (
                                            <PromoCallout
                                                priceData={priceData}
                                                product={productWithFilteredVariants}
                                            />
                                        ) : null
                                    }
                                    {...styles.price}
                                    isBold={false}
                                    isLarge={isPriceLarge}
                                    isToHide={isServer}
                                />
                            </Stack>
                        ))}
                </Box>
            </Link>

            {/* Product Badge including tag */}
            {showProductImages && product?.representedProduct?.c_tag && (
                <HStack {...styles.badgeGroup}>
                    <WrapperBadgeLink link={automatedTagLink}>
                        <Badge
                            {...styles.badgeCustomStyle}
                            data-testid="product-badge"
                            color={product?.representedProduct?.c_tagColor}
                            onClick={() =>
                                triggerClickFamilyLabel(
                                    product?.representedProduct?.c_tag,
                                    automatedTagLink
                                )
                            }
                        >
                            {product?.representedProduct?.c_tag}
                        </Badge>
                    </WrapperBadgeLink>
                </HStack>
            )}

            {/* Quick button with drawer */}
            {isCompleteTheLook && <CompleteTheLook product={product} isQuickShop={true} />}

            {/* Swatches List */}
            {showProductColorVariations &&
                variationAttributes
                    ?.filter(({id}) => selectableAttributeId === id)
                    ?.map(
                        ({id, values}) => {
                            // 1. Prepare the sorted values (Selected first, then others)
                            const sortedValues = [
                                ...values.filter(({ value }) => value === selectableAttributeValue),
                                ...values.filter(({ value }) => value !== selectableAttributeValue),
                            ];

                            const maxVisible = 4;
                            const visibleValues = sortedValues.slice(0, maxVisible);
                            const remainingCount = sortedValues.length - maxVisible;

                            // Render variations swatches only if more than 1 variant
                            return values?.length > 1 && (
                                <List
                                    key={id}
                                    display="flex"
                                    flexWrap={'wrap'}
                                    alignItems="center" // Ensures the "+ x items" aligns with swatches
                                    gap={6}
                                    {...styles.colorList}
                                    pl={paddingLeft}
                                    pr={paddingRight}
                                    className="tilescolors"
                                >
                                    {visibleValues.map(({ href, swatch, value, colorLabel, colorRegroupment, name }) => {
                                        const colorName = `${colorRegroupment} ${colorLabel}`.trim();
                                        const ariaLabelColorName = colorName || name || value;

                                        const content = swatch ? (
                                            <Box
                                                {...styles.colorChip}
                                                backgroundImage={`url(${swatch?.disBaseLink || swatch.link})`}
                                            >
                                                <Text as="span" className="sr-only">
                                                    {name || value}
                                                </Text>
                                            </Box>
                                        ) : (
                                            <Box>{name || value}</Box>
                                        );

                                        return (
                                            <ListItem key={value}>
                                                <Box
                                                    as="span"
                                                    data-url={btoa(removeQueryParamsFromPath(href, [
                                                        COLOR_ATTRIBUTE_ID,
                                                        SIZE_ATTRIBUTE_ID
                                                    ]))}
                                                    aria-label={intl.formatMessage(
                                                        {
                                                            id: 'product_tile.assistive_msg.go_to_color_variant',
                                                            defaultMessage: 'Go to product page - color variant: {color}'
                                                        },
                                                        { color: ariaLabelColorName }
                                                    )}
                                                    data-focusable-block // skip navigation on none-visible carousel slides
                                                    role="button"
                                                    tabIndex={0}
                                                    {...baseLinkStyles}
                                                    {...styles.colorLink}
                                                    onClick={handleColorSwatchesClick}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault()
                                                            handleColorSwatchesClick(e)
                                                        }
                                                    }}
                                                >
                                                    {content}
                                                </Box>
                                            </ListItem>
                                        );
                                    })}

                                    {/* 2. Dynamic counter with singular/plural support */}
                                    {remainingCount > 0 && (
                                        <ListItem>
                                            <Box
                                                as="span"
                                                data-url={btoa(productUrl)}
                                                data-focusable-block
                                                role="button"
                                                tabIndex={0}
                                                className="chakra-link"
                                                {...baseLinkStyles}
                                                {...styles.remainingColorsLink}
                                                onClick={handleColorSwatchesClick}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault()
                                                        handleColorSwatchesClick(e)
                                                    }
                                                }}
                                            >
                                                <FormattedMessage
                                                    description="# remaining color(s) "
                                                    defaultMessage="+{colorCount}"
                                                    values={{colorCount: remainingCount}}
                                                    id="product_tile.num_of_remaining_colors"
                                                />
                                            </Box>
                                        </ListItem>
                                    )}
                                </List>
                            );
                        }
                    )}

                {/* Favourite */}
                {!(productType.isBundle || productType.isSet) && (
                    <FavouriteIcon
                        isFavourite={isFavourite}
                        localizedProductName={localizedProductName}
                        onFavouriteToggle={onFavouriteToggle}
                        styles={styles.absoluteFavIcon}
                        className="tilesfavorite"
                    />
                )}
        </Box>
    )
}

ProductTile.displayName = 'ProductTile'

ProductTile.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.shape({
        currency: PropTypes.string,
        image: PropTypes.shape({
            alt: PropTypes.string,
            disBaseLink: PropTypes.string,
            link: PropTypes.string
        }),
        imageGroups: PropTypes.array,
        price: PropTypes.number,
        priceRanges: PropTypes.array,
        tieredPrices: PropTypes.array,
        c_tag: PropTypes.string,
        c_tagColor: PropTypes.string,
        // `name` is present and localized when `product` is provided by a RecommendedProducts component
        // (from Shopper Products `getProducts` endpoint), but is not present when `product` is
        // provided by a ProductList component.
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=getProducts
        name: PropTypes.string,
        // `productName` is localized when provided by a ProductList component (from Shopper Search
        // `productSearch` endpoint), but is NOT localized when provided by a RecommendedProducts
        // component (from Einstein Recommendations `getRecommendations` endpoint).
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=productSearch
        // See: https://developer.salesforce.com/docs/commerce/einstein-api/references/einstein-api-quick-start-guide?meta=getRecommendations
        // Note: useEinstein() transforms snake_case property names from the API response to camelCase
        productName: PropTypes.string,
        productId: PropTypes.string,
        productPromotions: PropTypes.array,
        representedProduct: PropTypes.object,
        hitType: PropTypes.string,
        variationAttributes: PropTypes.array,
        variationGroups: PropTypes.array,
        variationValues: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        variants: PropTypes.array,
        type: PropTypes.shape({
            set: PropTypes.bool,

            bundle: PropTypes.bool,
            item: PropTypes.bool
        }),
        id: PropTypes.string
    }),
    /**
     * Display the product as a favourite.
     */
    isFavourite: PropTypes.bool,
    /**
     * Callback function to be invoked when the user
     * interacts with favourite icon/button.
     */
    onFavouriteToggle: PropTypes.func,
    /**
     * When displaying a master/variant product, this value represents the variation attribute that is displayed
     * as a swatch below the main image. The default for this property is `color`.
     */
    selectableAttributeId: PropTypes.string,
    dynamicImageProps: PropTypes.object,
    /**
     * Determines whether to display a skeleton over personalizable data (e.g., pricing and promotions) during data refresh.
     */
    isRefreshingData: PropTypes.bool,
    /**
     * Determines if the main and hover images should be switched.
     */
    isSwitchedImages: PropTypes.bool,
    mainImageType: PropTypes.string,
    hoverImageType: PropTypes.string,
    index: PropTypes.number,
    listHasNoMargin: PropTypes.bool,
    focusRef: PropTypes.object,
    showProductImages: PropTypes.bool,
    showProductNames: PropTypes.bool,
    showProductPrices: PropTypes.bool,
    showProductColorVariations: PropTypes.bool,
    isFavIconInTitle: PropTypes.bool,
    isPriceLarge: PropTypes.bool,
    recommender: PropTypes.string,
    isImageLazyLoading: PropTypes.bool,
    titleHeading: PropTypes.string,
    isCompleteTheLook: PropTypes.bool
}

export default React.memo(ProductTile)

const shouldShowPromoCallout = (productWithFilteredVariants) => {
    return productWithFilteredVariants.representedProduct && productWithFilteredVariants.variants
        ? Boolean(productWithFilteredVariants.variants.find((variant) => variant.productPromotions))
        : Boolean(productWithFilteredVariants.productPromotions)
}
